import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { ContextTypes } from "api/model/dashboard/DashboardTab";
import { Button } from "primereact/button";
import useDashboardStore from "store/dashboard-store";
import {
  getAdjustedCurrentTimeInstant,
  sanitizeAdjustedTimeByContext,
} from "components/fragments/dashboard/utils";
import { useCompanyTimezone } from "hooks/useTimezone";
import { Instant } from "@js-joda/core";
import { toInstant } from "utils/time-utils";

interface TabContextMenuProps {
  tabId: number;
}

const TabContextMenu = ({ tabId }: TabContextMenuProps) => {
  const tabContextConfig = useDashboardStore(state => state.getTabContextConfig(tabId) || {});
  const setTabContextConfig = useDashboardStore(state => state.setTabContextConfig);
  const triggerTabRefresh = useDashboardStore(state => state.triggerTabRefresh);

  const companyTz = useCompanyTimezone();

  const adjustedTime = sanitizeAdjustedTimeByContext(
    getAdjustedCurrentTimeInstant(tabContextConfig.config || {}, companyTz),
    tabContextConfig.contextType
  );

  const [referenceTime, setReferenceTime] = useState<Date | undefined>(
    defaultDate(tabContextConfig.referenceTime, adjustedTime.referenceTime)
  );
  const [from, setFrom] = useState<Date | undefined>(
    defaultDate(tabContextConfig.from, adjustedTime.dateFrom)
  );

  const [to, setTo] = useState<Date | undefined>(defaultDate(tabContextConfig.to, adjustedTime.dateTo));

  const updateStore = () => {
    setTabContextConfig(tabId, {
      ...tabContextConfig,
      from: toInstant(from)?.toString(),
      to: toInstant(to)?.toString(),
      referenceTime: toInstant(referenceTime).toString(),

      instantFrom: from?.toInstant(),
      instantTo: to?.toInstant(),
      instantReferenceTime: referenceTime?.toInstant(),
    });
    triggerTabRefresh();
  };

  const handleRefTimeChange = (date: Date) => {
    setReferenceTime(date);
    setFrom(undefined);
    setTo(undefined);
  };

  const handleFromChange = (date: Date) => {
    setFrom(date);
    setReferenceTime(undefined);
  };

  const handleToChange = (date: Date) => {
    setTo(date);
    setReferenceTime(undefined);
  };

  return (
    <div className="flex flex-row flex-wrap gap-2">
      {tabContextConfig?.contextType === ContextTypes.RANGE_DATE_SELECTION && (
        <div className="flex flex-row flex-wrap gap-2">
          <CalendarWrapper
            id="dateFrom"
            value={from}
            onChange={handleFromChange}
            showTime
            className="align-self-center"
            debounceTimeInMs={100}
            tooltip={T("word.date-from")}
            tooltipOptions={{ position: "bottom" }}
          />

          <CalendarWrapper
            id="dateTo"
            value={to}
            onChange={handleToChange}
            showTime
            className="align-self-center"
            debounceTimeInMs={100}
            tooltip={T("word.date-to")}
            tooltipOptions={{ position: "bottom" }}
          />
        </div>
      )}
      {(tabContextConfig?.contextType === ContextTypes.SINGLE_DATE_SELECTION_WITH_CONFIG ||
        tabContextConfig?.contextType === ContextTypes.SINGLE_DATE_SELECTION) && (
        <CalendarWrapper
          id="referenceTime"
          value={referenceTime}
          onChange={e => handleRefTimeChange(new Date(e))}
          className="align-self-center"
          debounceTimeInMs={100}
          tooltip={T("load-conf.reference-time")}
          tooltipOptions={{ position: "bottom" }}
        />
      )}
      {tabContextConfig?.contextType === ContextTypes.YEARS_SELECTION && (
        <CalendarWrapper
          id="years"
          view="year"
          value={from}
          onChange={e => {
            // TODO: itariu negerai cia gali veikti kai display tz != browser
            const selectedYear = e.getFullYear();
            setFrom(new Date(selectedYear, 0, 1));
            const newTo = new Date(selectedYear + 1, 0, 1);
            newTo.setMinutes(newTo.getMinutes() - 1);
            setTo(newTo);
          }}
          dateFormat="yy"
          className="align-self-center"
          debounceTimeInMs={100}
          showTime={false}
          tooltip={T("time.years")}
          tooltipOptions={{ position: "bottom" }}
        />
      )}

      <Button
        icon="pi pi-sync"
        tooltip={T("action.fetch-data")}
        tooltipOptions={{ position: "bottom" }}
        onClick={updateStore}
        className="p-button-info align-self-center"
      />
    </div>
  );
};

const defaultDate = (date: string | undefined, fallbackDate: Instant | undefined) => {
  if (date) {
    return new Date(date);
  }

  if (fallbackDate) {
    return new Date(fallbackDate?.toDate());
  }

  return undefined;
};

export default TabContextMenu;
